import React, { useEffect, useState, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster/dist/leaflet.markercluster';
import LoadingSpinner from '../../utils/LoadingSpinner';
import i18n from "../Translations/i18n/i18n";
import { loadLimitedLocations } from "../../utils/Utils";
import Legend from '../Layout/MapLegend';
import { getAnnouncements } from '../../utils/AWSAPI';
import { t } from 'i18next';

const Home = () => {
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [announcements, setAnnouncements] = useState(null);
  const [markersLoaded, setMarkersLoaded] = useState(false);

  const mapRef = useRef(null);
  const markerClusterGroupRef = useRef(null);

  useEffect(() => {
    const loadLanguage = () => {
      const language = localStorage.getItem("language");
      if (language) {
        i18n.changeLanguage(language);
      }
    };
    loadLanguage();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locations = await loadLimitedLocations("False", localStorage.getItem("user_identifier"));
        setData(locations);
        const announcements = await getAnnouncements();
        setAnnouncements(announcements);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (!isLoading && !mapRef.current) {
      mapRef.current = L.map('map').setView([43.653225, -79.383186], 10);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(mapRef.current);

      markerClusterGroupRef.current = L.markerClusterGroup();
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && data.length > 0 && !markersLoaded && mapRef.current && markerClusterGroupRef.current) {
      const markers = [];
      data.forEach(loc => {
        const lat = loc.latitude;
        const lon = loc.longitude;

        // Shoutout https://github.com/pointhi/leaflet-color-markers :D

        const redIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
        
        const yellowIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });
        
        const greenIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        // green = no incidents
        // yellow = open incidents
        // red = critical incidents

        var icon = loc.totalCount == 0 ? greenIcon : loc.p1Count > 0 ? redIcon : yellowIcon;

        const marker = L.marker([lat, lon], { icon: icon })
          .on('click', () => {
            window.open(`dashboard?location=${loc.sys_id}&locationName=${loc.name}`, '_blank');
          });
        markers.push(marker);
      });

      markerClusterGroupRef.current.addLayers(markers);
      mapRef.current.addLayer(markerClusterGroupRef.current);
      setMarkersLoaded(true);
    }
  }, [isLoading, data, markersLoaded]);

  return (
    <div className="App">
      <div className="map-container" style={{ position: 'relative' }}>
        <LoadingSpinner show={isLoading || !markersLoaded} absolutePosition={true}/>

        {!isLoading && (
          <div>
            {announcements?.length > 0 ? (
              <a href={"/announcements"} target={"_blank"} style={{textDecoration: 'none'}}>
                <div style={{ 
                  backgroundColor: 'rgb(238, 208, 38)', 
                  padding: '10px', 
                  color: 'black', 
                  fontWeight: 'bold', 
                  position: 'absolute', 
                  left: '50%', 
                  transform: 'translateX(-50%)', 
                  zIndex: 1000,
                  width: 'calc(100% - 20px)'
                }}>
                  {t('ActiveAnnouncements')}
                </div>
              </a>
            ) : (
              <div style={{ 
                backgroundColor: 'rgb(238, 208, 38)', 
                padding: '10px', 
                color: 'black', 
                fontWeight: 'bold', 
                position: 'absolute', 
                left: '50%', 
                transform: 'translateX(-50%)', 
                zIndex: 1000,
                width: 'calc(100% - 20px)'
              }}>
                {t('NoAnnouncements')}
              </div>
            )}
          </div>
        )}

        <div id="map" style={{ height: '100vh' }}></div>
        <Legend/>
      </div>
    </div>
  );
};

export default React.memo(Home);